import { motion, AnimatePresence, useReducedMotion } from 'motion/react';
import { useEffect, useMemo } from 'react';
import type { Pin, Memory } from '@/types';
import { Heart } from '@/components/ui/Heart';
import { Button } from '@/components/ui/Button';
import { FloatingHearts } from '@/components/motifs/FloatingHearts';
import { useUI } from '@/lib/store';

/**
 * The unlock moment.
 *
 * Choreographed in five beats over ~1.8s, then a held tableau:
 *
 *   t=0      backdrop blooms in, soft cream→blush radial glow appears
 *   t=200ms  sealed lavender heart scales up and tilts away as wax dissolves
 *   t=300ms  ~14 wax particles spray outward in a hemisphere and fade
 *   t=600ms  blush heart blooms in (overshoot spring), ambient breathing begins
 *   t=900ms  pin/memory title rises into view in italic display serif
 *   t=1300ms handwritten line resolves
 *   t=1600ms two soft buttons fade up
 *
 * Held for up to 8s, dismissable by tap-out, by pressing escape, or by either
 * button. The celebration is a leaf component — mounts on demand, unmounts on
 * dismiss — so the perpetual motion inside (FloatingHearts, breathing heart)
 * never leaks into the parent layout.
 */

interface CelebrationItem {
  pin: Pin;
  memory?: Memory;
}

interface Props {
  item: CelebrationItem | null;
  onDismiss: () => void;
}

const SPRING = { type: 'spring' as const, stiffness: 120, damping: 22 };
const SPRING_OVERSHOOT = { type: 'spring' as const, stiffness: 180, damping: 16 };

export function UnlockCelebration({ item, onDismiss }: Props) {
  const selectPin = useUI((s) => s.selectPin);
  const reduced = useReducedMotion();

  useEffect(() => {
    if (!item) return;
    const t = window.setTimeout(onDismiss, 8000);
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onDismiss(); };
    window.addEventListener('keydown', onKey);
    return () => { window.clearTimeout(t); window.removeEventListener('keydown', onKey); };
  }, [item, onDismiss]);

  function open() {
    if (!item) return;
    selectPin(item.pin.id);
    onDismiss();
  }

  const title = item?.memory ? formatMemoryTitle(item.memory, item.pin) : item?.pin.title;
  const subline = item?.memory ? memoryLine(item.memory) : 'It\'s time.';

  return (
    <AnimatePresence>
      {item && (
        <motion.div
          className="celebration"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.5, ease: [0.16, 1, 0.3, 1] }}
          onClick={onDismiss}
          role="dialog"
          aria-live="polite"
          aria-label={`unlocked: ${title ?? ''}`}
        >
          <div aria-hidden className="celebration__glow" />
          <FloatingHearts density={reduced ? 0 : 8} duration={[14, 22]} />

          <div
            className="celebration__panel"
            onClick={(e) => e.stopPropagation()}
            data-reduced={reduced ? 'true' : 'false'}
          >
            <div className="celebration__heartstack">
              {!reduced && <SealRing />}
              <SealedHeart />
              {!reduced && <WaxParticles />}
              <BloomingHeart />
            </div>

            <motion.h2
              initial={{ opacity: 0, y: 14 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.9, ...SPRING }}
              className="celebration__title"
            >
              {title}
            </motion.h2>

            <motion.p
              initial={{ opacity: 0, y: 6 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.3, duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
              className="celebration__hand"
            >
              {subline}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 1.6, ...SPRING }}
              className="celebration__actions"
            >
              <Button variant="ghost" size="md" onClick={onDismiss}>not now</Button>
              <Button variant="primary" size="md" onClick={open}>open</Button>
            </motion.div>
          </div>

          <style>{css}</style>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

// ── beats ──────────────────────────────────────────────────────────────────

function SealRing() {
  return (
    <motion.div
      className="celebration__ring"
      initial={{ opacity: 1, scale: 1 }}
      animate={{ opacity: 0, scale: 1.8, rotate: 14 }}
      transition={{ delay: 0.2, duration: 0.7, ease: [0.32, 0.72, 0.24, 1] }}
      aria-hidden
    />
  );
}

function SealedHeart() {
  return (
    <motion.div
      className="celebration__sealed"
      initial={{ opacity: 1, scale: 1, rotate: 0 }}
      animate={{ opacity: 0, scale: 1.35, rotate: 22 }}
      transition={{ delay: 0.2, duration: 0.6, ease: [0.32, 0.72, 0.24, 1] }}
      aria-hidden
    >
      <Heart variant="sealed" size={132} />
    </motion.div>
  );
}

function BloomingHeart() {
  return (
    <motion.div
      className="celebration__heart"
      initial={{ opacity: 0, scale: 0.55, rotate: -6 }}
      animate={{ opacity: 1, scale: [0.55, 1.18, 1], rotate: 0 }}
      transition={{ delay: 0.6, duration: 0.85, times: [0, 0.62, 1], ease: [0.16, 1, 0.3, 1] }}
      aria-hidden
    >
      <Heart size={132} className="a-breathe" />
    </motion.div>
  );
}

function WaxParticles() {
  // Sprayed in a hemisphere from where the seal was. Stable randoms via
  // useMemo so React StrictMode doesn't re-roll the spray on each mount.
  const particles = useMemo(() => {
    return Array.from({ length: 14 }, (_, i) => {
      const angle = -Math.PI + (i / 13) * Math.PI;          // -π … 0 (upward arc)
      const distance = 70 + (i % 4) * 18 + (i * 13 % 17);
      const size = 4 + (i % 5);
      const tone = i % 3 === 0 ? 'var(--c-rose)' : i % 3 === 1 ? 'var(--c-rose-deep)' : 'var(--c-mauve)';
      return {
        id: i, angle, distance, size, tone,
        delay: 0.3 + (i % 7) * 0.02,
        rot: (i * 47) % 90 - 45,
      };
    });
  }, []);

  return (
    <div className="celebration__particles" aria-hidden>
      {particles.map((p) => (
        <motion.span
          key={p.id}
          className="celebration__particle"
          style={{ width: p.size, height: p.size, background: p.tone }}
          initial={{ x: 0, y: 0, opacity: 1, scale: 1, rotate: 0 }}
          animate={{
            x: Math.cos(p.angle) * p.distance,
            y: Math.sin(p.angle) * p.distance,
            opacity: 0,
            scale: 0.35,
            rotate: p.rot,
          }}
          transition={{ delay: p.delay, duration: 0.85, ease: [0.16, 1, 0.3, 1] }}
        />
      ))}
    </div>
  );
}

// ── copy ───────────────────────────────────────────────────────────────────

function formatMemoryTitle(m: Memory, p: Pin): string {
  if (m.kind === 'letter') return `a letter, at ${p.title}`;
  if (m.kind === 'audio')  return `a recording, at ${p.title}`;
  if (m.kind === 'photo')  return `a picture, at ${p.title}`;
  return `a note, at ${p.title}`;
}

function memoryLine(m: Memory): string {
  switch (m.kind) {
    case 'letter': return 'sealed for today.';
    case 'photo':  return 'kept for now.';
    case 'audio':  return 'pressed into wax.';
    case 'note':   return 'left here for you.';
  }
}

// ── styles ─────────────────────────────────────────────────────────────────

const css = `
.celebration {
  position: fixed; inset: 0; z-index: 70;
  display: grid; place-items: center;
  background: rgba(92, 75, 87, 0.32);
  backdrop-filter: blur(14px) saturate(140%);
  -webkit-backdrop-filter: blur(14px) saturate(140%);
  cursor: pointer;
  padding: var(--space-5);
}
/* Soft radial glow behind the panel — intimate, candlelit, not festival */
.celebration__glow {
  position: absolute; inset: -10%;
  background:
    radial-gradient(circle at 50% 45%, var(--c-blush) 0%, transparent 45%),
    radial-gradient(circle at 50% 60%, var(--c-peach) 0%, transparent 55%);
  opacity: 0.55;
  pointer-events: none;
  filter: blur(40px);
  animation: drift 9s var(--ease-soft) infinite;
}
.celebration__panel {
  position: relative;
  cursor: auto;
  display: flex; flex-direction: column; align-items: center;
  gap: var(--space-4);
  padding: var(--space-8) var(--space-6) var(--space-6);
  max-width: 480px; width: 100%;
  text-align: center;
}
.celebration__heartstack {
  position: relative;
  width: 132px; height: 132px;
  display: grid; place-items: center;
}
.celebration__heartstack > * {
  position: absolute; inset: 0;
  display: grid; place-items: center;
}
.celebration__ring {
  border: 1px dashed var(--c-locked-edge);
  border-radius: 50%;
  margin: -16px;
  inset: 0;
}
.celebration__particles {
  position: absolute; inset: 0;
  display: grid; place-items: center;
  pointer-events: none;
}
.celebration__particle {
  position: absolute;
  border-radius: 50%;
  box-shadow: 0 0 6px rgba(232, 162, 176, 0.4);
}
.celebration__title {
  font-family: var(--font-display);
  font-style: italic;
  font-size: clamp(1.8rem, 5vw, 2.6rem);
  font-weight: 400;
  color: var(--c-ink);
  letter-spacing: -0.02em;
  line-height: 1.05;
  margin: var(--space-2) 0 0;
  max-width: 18ch;
}
.celebration__hand {
  font-family: var(--font-hand);
  font-size: 1.45rem;
  color: var(--c-rose-deep);
  margin: 0;
  letter-spacing: 0.005em;
}
.celebration__actions {
  display: flex; gap: var(--space-3);
  margin-top: var(--space-3);
}

/* Reduced motion: skip the choreography; render the resolved tableau */
.celebration__panel[data-reduced="true"] .celebration__sealed,
.celebration__panel[data-reduced="true"] .celebration__ring,
.celebration__panel[data-reduced="true"] .celebration__particles { display: none; }
.celebration__panel[data-reduced="true"] .celebration__heart { animation: none; }
`;
